// highlight
import './utils/highlight';

// editor
import 'react-quill-new/dist/quill.snow.css';

import { BrowserRouter } from 'react-router';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
// material
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
// redux
import { store, persistor } from './redux/store';
// contexts
import { SettingsProvider } from './contexts/SettingsContext';
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';
// components
import LoadingScreen from './components/util/LoadingScreen';

import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

import TimeAgo from 'javascript-time-ago';
import type { ReactElement } from 'react';

import en from 'javascript-time-ago/locale/en.json';
import ThemeConfig from 'theme';
import NotistackProvider from 'components/util/NotistackProvider';
import { LanguageProvider } from 'contexts/LanguageContext';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from 'ErrorFallback';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';

const rootMap = new WeakMap<Element | DocumentFragment, ReturnType<typeof createRoot>>();

// SurveyJS Creator still calls ReactDOM.render/unmountComponentAtNode which are no longer available in React 19.
// TODO: Remove this when we renew our SurveyJS license and update to the latest version of the packages
if (!('render' in ReactDOM)) {
  (ReactDOM as any).render = (element: ReactElement, container: Element | DocumentFragment) => {
    const existingRoot = rootMap.get(container);
    const root = existingRoot ?? createRoot(container as Element);

    if (!existingRoot) {
      rootMap.set(container, root);
    }

    root.render(element);
    return root;
  };

  (ReactDOM as any).unmountComponentAtNode = (container: Element | DocumentFragment) => {
    const existingRoot = rootMap.get(container);
    if (!existingRoot) {
      return false;
    }

    existingRoot.unmount();
    rootMap.delete(container);
    return true;
  };
}

TimeAgo.addDefaultLocale(en);

// ----------------------------------------------------------------------

const root = createRoot(document.getElementById('root')!);
root.render(
  <SettingsProvider>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeConfig>
        <NotistackProvider>
          <ReduxProvider store={store}>
            <PersistGate loading={<LoadingScreen />} persistor={persistor}>
              <LanguageProvider>
                <CollapseDrawerProvider>
                  <BrowserRouter>
                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                      <App />
                    </ErrorBoundary>
                  </BrowserRouter>
                </CollapseDrawerProvider>
              </LanguageProvider>
            </PersistGate>
          </ReduxProvider>
        </NotistackProvider>
      </ThemeConfig>
    </LocalizationProvider>
  </SettingsProvider>
);

serviceWorkerRegistration.unregister();
